import { useState, useEffect } from "react"

import { useAppDispatch, useAppSelector } from "hooks/store"
import Cookies from "js-cookie"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { personalForm } from "store/auth/auth.selectors"
import {
  setAuthData,
  setActivePageSignUp,
  setPersonalForm,
} from "store/auth/auth.slice"
import { IAuthData } from "ts/interfaces/Auth"
import { getAuthCookieDomain } from "utils/domain"
import {
  mergePostLoginRedirectFromRouterState,
  takeIntendedPathAfterLogin,
} from "utils/postLoginRedirect"

interface UseCleverSSOProps {
  userType: "teacher" | "student"
  isSignup?: boolean
}

export const useCleverSSO = ({
  userType,
  isSignup = false,
}: UseCleverSSOProps) => {
  const [isCleverSSOLoading, setIsCleverSSOLoading] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const currentPersonalForm = useAppSelector(personalForm)

  useEffect(() => {
    mergePostLoginRedirectFromRouterState(location.state)
  }, [location.key])

  useEffect(() => {
    const isCleverSSO = searchParams.get("sso") === "clever"
    // hasCleverError also renders for TeachHub/ClassLink's ?error=email_missing /
    // ?error=wrong_user_type / ?error=authentication_failed on any screen
    // where both loaders are mounted (teacher and student alike) — same
    // codes, same messages — so useOpenIdSSO deliberately does not
    // duplicate this detection (see useOpenIdSSO.ts).
    const hasCleverError =
      searchParams.get("error") &&
      ["email_missing", "wrong_user_type", "authentication_failed"].includes(
        searchParams.get("error")!
      )

    if (isCleverSSO || hasCleverError) {
      setIsCleverSSOLoading(true)
    }
  }, [searchParams])

  const clearCleverSearchParams = () => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete("sso")
    newSearchParams.delete("error")
    navigate(
      `${location.pathname}${
        newSearchParams.toString() ? `?${newSearchParams.toString()}` : ""
      }`,
      { replace: true }
    )
  }

  const handleCleverReset = () => {
    setIsCleverSSOLoading(false)
    clearCleverSearchParams()
  }

  const handleCleverAuthSuccess = (authData: IAuthData) => {
    dispatch(setAuthData(authData))
    Cookies.remove("auth_data", { domain: getAuthCookieDomain() })
    setIsCleverSSOLoading(false)
    clearCleverSearchParams()

    if (!authData.finalized) {
      if (userType === "teacher") {
        dispatch(
          setPersonalForm({
            ...currentPersonalForm,
            first_name: authData.first_name ?? currentPersonalForm.first_name,
            last_name: authData.last_name ?? currentPersonalForm.last_name,
          })
        )
        if (isSignup) {
          dispatch(setActivePageSignUp(2))
        } else {
          navigate(RoutePath.SIGNUP)
          dispatch(setActivePageSignUp(2))
        }
      } else {
        if (isSignup) {
          dispatch(setActivePageSignUp(3))
        } else {
          navigate(RoutePath.SIGNUP, { state: "login" })
          dispatch(setActivePageSignUp(3))
        }
      }
    } else {
      const redirect = takeIntendedPathAfterLogin()
      queueMicrotask(() => navigate(redirect ?? RoutePath.HOMEPAGE, { replace: true }))
    }
  }

  return {
    isCleverSSOLoading,
    handleCleverReset,
    handleCleverAuthSuccess,
  }
}
